// src/config/modules.js
//
// Реестр выключаемых модулей интерфейса.
// Включение/выключение хранится в shop.moduleConfig = { key: bool }.
// Ключа нет → берётся `default` (включено). Поэтому у текущих магазинов
// (пустой moduleConfig) включено всё.
//
// Добавить новый модуль (напр. checker, on_pedestal) — просто добавить строку
// сюда + продублировать ключ в ALLOWED_MODULES на бэке. Миграция не нужна.

export const MODULES = [
  { key: 'products',      labelKey: 'modules.products',      icon: 'package',   default: true,  perm: 'products',      actions: ['stockOps'], requires: [] },
  { key: 'categories',    labelKey: 'modules.categories',    icon: 'folder',    default: true,  perm: 'categories',    requires: [] },
  { key: 'cashier',       labelKey: 'modules.cashier',       icon: 'cashier',   default: true,  perm: 'cashier',       actions: ['loyalty', 'discounts'], requires: [] },
  { key: 'analytics',     labelKey: 'modules.analytics',     icon: 'chart',     default: true,  perm: 'analytics',     requires: [] },
  { key: 'employees',     labelKey: 'modules.employees',     icon: 'users',     default: true,  perm: null,            ownerOnly: true, requires: [] },
  { key: 'counterparties',labelKey: 'modules.counterparties',icon: 'truck',     default: true,  perm: null, visPerm: 'counterparties', requires: [] },
  { key: 'wholesale',     labelKey: 'modules.wholesale',     icon: 'fileText',  default: true,  perm: null, visPerm: 'wholesale', requires: ['counterparties'] },
  { key: 'ai',            labelKey: 'modules.ai',            icon: 'sparkles',  default: true,  perm: null, visPerm: 'ai', requires: [] },
  { key: 'aiAssistant',   labelKey: 'modules.aiAssistant',   icon: 'sparkles',  default: true,  perm: 'analytics',     requires: [] },
  { key: 'retailDebt',    labelKey: 'modules.retailDebt',    icon: 'wallet',    default: false, perm: 'cashier',       requires: [] },
  { key: 'marking',       labelKey: 'modules.marking',       icon: 'qr',        default: false, perm: 'products',      requires: ['products'] },
  { key: 'services',      labelKey: 'modules.services',      icon: 'sparkles',  default: false, perm: 'products',      requires: ['products'] },
  { key: 'variants',      labelKey: 'modules.variants',      icon: 'copy',      default: true,  perm: 'products',      requires: ['products'] },
  { key: 'composition',   labelKey: 'modules.composition',   icon: 'package',   default: true,  perm: 'products',      requires: ['products'] },
  { key: 'purchases',     labelKey: 'modules.purchases',     icon: 'package',   default: true,  perm: 'products',      requires: ['products'] },
  { key: 'returns',       labelKey: 'modules.returns',       icon: 'refresh',   default: true,  perm: 'products',      requires: ['products'] },
  { key: 'writeoffs',     labelKey: 'modules.writeoffs',     icon: 'trash',     default: true,  perm: 'products',      requires: ['products'] },
  { key: 'stocktake',     labelKey: 'modules.stocktake',     icon: 'list',      default: false, perm: 'products',      requires: ['products'] },
  { key: 'ownBarcodes',   labelKey: 'modules.ownBarcodes',   icon: 'sparkles',  default: false, perm: 'products',      requires: ['products'] },
  { key: 'perishable',    labelKey: 'modules.perishable',    icon: 'alert',     default: false, perm: 'products',      requires: ['products'] },
  { key: 'multiLevelCashier', labelKey: 'modules.multiLevelCashier', icon: 'folder', default: false, perm: 'cashier', requires: ['cashier', 'categories'] },
  { key: 'waiter',        labelKey: 'modules.waiter',        icon: 'cashier',   default: false, perm: 'waiter',        requires: [] },
  { key: 'kitchen',       labelKey: 'modules.kitchen',       icon: 'list',      default: false, perm: 'kitchen',       requires: ['waiter'] },
];

export const MODULE_BY_KEY = MODULES.reduce((m, x) => (m[x.key] = x, m), {});

// Чистая функция: включён ли модуль у магазина (с учётом зависимостей).
// shop может быть null (ещё не загружен) — тогда считаем включённым (default).
export function isModuleOn(shop, key) {
  const reg = MODULE_BY_KEY[key];
  if (!reg) return true; // неизвестный ключ — не прячем
  // Зависимости: если выключена зависимость — модуль тоже выключен
  if (reg.requires && reg.requires.some(dep => !isModuleOn(shop, dep))) return false;
  const cfg = shop && shop.moduleConfig && typeof shop.moduleConfig === 'object'
    ? shop.moduleConfig : {};
  const v = cfg[key];
  return v === undefined || v === null ? (reg.default !== false) : !!v;
}

// ─────────────────────────────────────────────────────────────────────────────
// Реестр как единый источник правды для прав.
//
// Две оси прав:
//   1) видимость модуля  → право = ключ модуля (visPerm). По умолчанию = key.
//   2) действия внутри   → m.actions: ['loyalty','stockOps','discounts', ...]
//
// Принцип «добавляем, не меняем»: новый модуль приносит свои права сам,
// страницы их выводят из реестра. Старые сотрудники не ломаются —
// старое групповое право (m.perm) работает как fallback (см. canSeeModule).
// ─────────────────────────────────────────────────────────────────────────────

// Подписи для прав-действий (i18n-ключи)
export const ACTION_LABELS = {
  loyalty:   'employees.permLoyalty',
  stockOps:  'employees.permStockOps',
  discounts: 'discounts.permDiscounts',
};

// Право на видимость модуля.
//  - ownerOnly → null (сотруднику не выдаётся, только владелец)
//  - perm === null и нет visPerm → null (публичный модуль, виден всем)
//  - иначе → собственное право модуля (по умолчанию = ключ)
export function moduleVisPerm(m) {
  if (!m || m.ownerOnly) return null;
  if (m.visPerm) return m.visPerm;
  if (m.perm == null) return null;     // публичный
  return m.key;                        // гранулярное право = ключ модуля
}

export function moduleActions(m) { return (m && m.actions) || []; }

// Список прав-видимости для страницы сотрудника (по одному на модуль, без дублей)
export function employeeModulePerms() {
  const seen = new Set();
  const out = [];
  for (const m of MODULES) {
    const vp = moduleVisPerm(m);
    if (!vp || seen.has(vp)) continue;
    seen.add(vp);
    out.push({ key: vp, labelKey: m.labelKey, requires: m.requires || [] });
  }
  return out;
}

// Список прав-действий (собираем m.actions со всех модулей, без дублей)
export function employeeActionPerms() {
  const seen = new Set();
  const out = [];
  for (const m of MODULES) {
    for (const a of moduleActions(m)) {
      if (seen.has(a)) continue;
      seen.add(a);
      out.push({ key: a, labelKey: ACTION_LABELS[a] || a });
    }
  }
  return out;
}

// МОСТ СОВМЕСТИМОСТИ: развернуть старые групповые права в гранулярные.
// Используется при ОТКРЫТИИ формы сотрудника, чтобы галочки отражали
// реальный доступ старого сотрудника (у него стоит, напр., products:true).
export function expandLegacyPerms(perms) {
  const out = { ...(perms || {}) };
  for (const m of MODULES) {
    const vp = moduleVisPerm(m);
    if (!vp) continue;
    // если у модуля есть старое групповое право и оно включено — включаем гранулярное
    if (m.perm && m.perm !== vp && out[m.perm] === true && !(vp in out)) {
      out[vp] = true;
    }
  }
  return out;
}

// ЦЕНТРАЛЬНАЯ проверка: видит ли пользователь модуль.
// ctx = { isOwner, can }  (can — функция из useAuth)
export function canSeeModule(shop, key, ctx) {
  if (!isModuleOn(shop, key)) return false;
  const m = MODULE_BY_KEY[key];
  if (!m) return true;                    // неизвестный ключ — не прячем
  if (ctx && ctx.isOwner) return true;    // владельцу всё
  if (m.ownerOnly) return false;          // только владелец
  const can = (ctx && ctx.can) || (() => false);
  const vp = moduleVisPerm(m);
  if (!vp && m.perm == null) return true; // публичный — виден всем (если такой модуль есть)
  if (vp && can(vp)) return true;         // право на модуль (гранулярно)
  return false;
}
